/**
 * Optional local spike/seizure detector run for AI Triage (S3).
 * Assistive only — stamps provenance; does not invent annotation confidence.
 */

import SpikeDetectionService from "../services/SpikeDetection";
import SeizureDetectionService from "../services/SeizureDetection";
import type { DetectedSpike, SeizureEvent } from "../types/detectionTypes";

export const LOCAL_DETECTOR_RUN_VERSION = "local-detectors-v1-s3";

export type LocalDetectorChannel = {
  name: string;
  data?: Float32Array | number[] | null;
};

export type LocalDetectorRunResult = {
  spikes: DetectedSpike[];
  seizures: SeizureEvent[];
  provenance: {
    algorithmVersion: string;
    modelNames: string[];
    channelCount: number;
    sampleRateHz: number;
    windowSamples: number;
  };
};

function toNumberArray(data: Float32Array | number[]): number[] {
  return Array.isArray(data) ? data : Array.from(data);
}

export function runLocalDetectors(input: {
  channels: LocalDetectorChannel[];
  sampleRateHz: number;
  maxChannels?: number;
}): LocalDetectorRunResult {
  const sampleRate = Number.isFinite(input.sampleRateHz) && input.sampleRateHz > 0
    ? input.sampleRateHz
    : 256;
  const maxChannels = Math.max(1, input.maxChannels ?? 8);
  const usable = input.channels
    .filter((ch) => ch.data && ch.data.length >= sampleRate)
    .slice(0, maxChannels);

  const signals = usable.map((ch) => toNumberArray(ch.data as Float32Array | number[]));
  const names = usable.map((ch, i) => ch.name || `Ch${i}`);

  const spikeResults = SpikeDetectionService.detectMultiChannelSpikes(
    signals,
    {
      amplitudeThreshold: 3,
      minDurationMs: 20,
      maxDurationMs: 200,
      samplingRate: sampleRate,
      refractoryPeriodMs: 100,
    },
    names,
  );

  const spikes: DetectedSpike[] = [];
  spikeResults.forEach((result, channelIndex) => {
    for (const spike of result.spikes) {
      spikes.push({
        id: `local-spike-${channelIndex}-${spike.peakTime.toFixed(3)}`,
        channelIndex,
        channelName: spike.channelName || names[channelIndex],
        time: spike.peakTime,
        sampleIndex: spike.peakIndex,
        amplitude: spike.amplitude,
        duration: spike.durationMs,
        confidence: spike.confidence,
        morphology: {
          riseTime: Math.max(1, (spike.peakTime - spike.startTime) * 1000),
          fallTime: Math.max(1, (spike.endTime - spike.peakTime) * 1000),
          sharpness: Math.min(1, Math.max(0, spike.confidence)),
        },
      });
    }
  });

  const seizureSvc = new SeizureDetectionService();
  const multi = signals.length
    ? seizureSvc.detectMultiChannelSeizures(signals, names, sampleRate)
    : { detections: [] as any[] };

  const clustered = multi.detections || [];

  const seizures: SeizureEvent[] = clustered.map((sz, index) => {
    const onsetMs = Number(sz.onsetTime ?? 0);
    const offsetMs = Number(
      sz.offsetTime ?? onsetMs + Math.max(10000, (sz.duration || 10) * 1000),
    );
    // SeizureDetection stores onset/offset in ms from recording start.
    const startTime = onsetMs / 1000;
    const endTime = offsetMs / 1000;
    const channelList: string[] = Array.isArray(sz.channels)
      ? sz.channels.map(String)
      : names.slice(0, 1);
    return {
      id: String(sz.seizureId || `local-sz-${index}-${startTime}`),
      startTime,
      endTime: Math.max(endTime, startTime + 0.5),
      duration: Math.max(0.5, endTime - startTime),
      peakProbability: Number(sz.confidence ?? 0.6),
      avgProbability: Number(sz.confidence ?? 0.6),
      channels: channelList
        .map((name) => names.indexOf(name))
        .filter((i) => i >= 0),
      type: "ictal" as const,
    };
  });

  return {
    spikes,
    seizures,
    provenance: {
      algorithmVersion: LOCAL_DETECTOR_RUN_VERSION,
      modelNames: ["local-spike-detector", "local-seizure-detector"],
      channelCount: usable.length,
      sampleRateHz: sampleRate,
      windowSamples: signals[0]?.length || 0,
    },
  };
}
